export const LIVE_EVENTS_LAYER_ID = "live_events";

function hasLiveCharacterData(presence) {
  return Array.isArray(presence?.characters)
    && presence.characters.some((character) => character?.status === "live");
}

export function liveEventsLayerEligible({
  accountReady,
  trackerEnabled,
  streamAvailable,
  presence,
} = {}) {
  return accountReady === true
    && trackerEnabled === true
    && streamAvailable === true
    && hasLiveCharacterData(presence);
}

export function liveEventsLayerVisibilityPatch(projection, eligible) {
  const visibleLayerIds = projection?.bridged?.filters?.layerIdsVisible;
  if (
    eligible === true
    || !Array.isArray(visibleLayerIds)
    || !visibleLayerIds.includes(LIVE_EVENTS_LAYER_ID)
  ) {
    return null;
  }
  return {
    _map_bridged: {
      filters: {
        layerIdsVisible: visibleLayerIds.filter(
          (layerId) => layerId !== LIVE_EVENTS_LAYER_ID,
        ),
      },
    },
  };
}

export function createLiveEventsLayerGate({
  readProjection,
  subscribeProjection,
  applyPatch,
  onEligibilityChange,
} = {}) {
  if (
    typeof readProjection !== "function"
    || typeof subscribeProjection !== "function"
    || typeof applyPatch !== "function"
    || (onEligibilityChange != null && typeof onEligibilityChange !== "function")
  ) {
    throw new TypeError("Live Events layer gate requires projection callbacks.");
  }

  let state = {
    accountReady: false,
    trackerEnabled: false,
    streamAvailable: false,
    presence: null,
  };
  let unsubscribe = null;
  let stopped = false;
  let applying = false;
  let publishedEligibility = null;

  function eligible() {
    return liveEventsLayerEligible(state);
  }

  function publishEligibility(nextEligibility) {
    if (publishedEligibility === nextEligibility) return;
    publishedEligibility = nextEligibility;
    onEligibilityChange?.(nextEligibility);
  }

  function reconcile() {
    if (stopped || applying) return false;
    const nextEligibility = eligible();
    publishEligibility(nextEligibility);
    const patch = liveEventsLayerVisibilityPatch(readProjection(), nextEligibility);
    if (!patch) return false;
    applying = true;
    try {
      applyPatch(patch);
    } finally {
      applying = false;
    }
    return true;
  }

  function setAccess(access) {
    state = {
      ...state,
      accountReady: access?.accountReady === true,
      trackerEnabled: access?.trackerEnabled === true,
    };
    reconcile();
  }

  function setStreamAvailable(streamAvailable) {
    state = { ...state, streamAvailable: streamAvailable === true };
    reconcile();
  }

  function setPresence(presence) {
    state = { ...state, presence };
    reconcile();
  }

  function start() {
    if (stopped || unsubscribe) return false;
    unsubscribe = subscribeProjection(reconcile);
    reconcile();
    return true;
  }

  function stop() {
    if (stopped) return false;
    stopped = true;
    unsubscribe?.();
    unsubscribe = null;
    publishEligibility(false);
    return true;
  }

  return Object.freeze({
    eligible,
    setAccess,
    setPresence,
    setStreamAvailable,
    start,
    stop,
  });
}
